import React, {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { isMobile } from "react-device-detect";
import { calculateFrameFaceTextureRepeat } from "./frameTextureMapping";

const moduleScriptPromises = new Map();
const MODEL_VIEWER_SCRIPT_SRC =
  "https://unpkg.com/@google/model-viewer@4.1.0/dist/model-viewer.min.js";
const MODEL_VIEWER_STYLE_ID = "frameViewer-ar-button-hide";

const loadModuleScript = (src) => {
  if (typeof document === "undefined") {
    return Promise.resolve();
  }

  if (moduleScriptPromises.has(src)) {
    return moduleScriptPromises.get(src);
  }

  const existingScript = document.querySelector(`script[src="${src}"]`);
  if (existingScript) {
    const resolvedPromise = Promise.resolve(existingScript);
    moduleScriptPromises.set(src, resolvedPromise);
    return resolvedPromise;
  }

  const scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "module";
    script.src = src;
    script.async = true;
    script.onload = () => resolve(script);
    script.onerror = () =>
      reject(new Error(`Failed to load module script: ${src}`));
    document.body.appendChild(script);
  });

  moduleScriptPromises.set(src, scriptPromise);
  return scriptPromise;
};

const ThreeJSCanvas = forwardRef(
  (
    {
      mat_style,
      width_mm,
      height_mm,
      frame_style,
      preview_url,
      maxWidth = 600,
      mat,
      setCanvasAssetsLoaded,
      hdr_environment,
      onARReady,
      launch_ar_mode = false,
      onARLaunched,
    },
    ref,
  ) => {
    const sceneRef = useRef(null);
    const frameGroupRef = useRef(null);
    const primaryViewerHostRef = useRef(null);
    const secondaryViewerHostRef = useRef(null);
    const viewerRefs = useRef([null, null]);
    const viewerBaseCleanupRef = useRef([null, null]);
    const viewerSlotStateRef = useRef([
      {
        browserUrl: null,
        token: 0,
        cleanupLoad: null,
        cleanupError: null,
      },
      {
        browserUrl: null,
        token: 0,
        cleanupLoad: null,
        cleanupError: null,
      },
    ]);
    const activeSlotRef = useRef(0);
    const committedPreviewRef = useRef({
      token: 0,
      slotIndex: 0,
      browserUrl: null,
      arUrl: null,
    });
    const pendingPreviewRef = useRef(null);
    const renderTokenRef = useRef(0);
    const discardedTokensRef = useRef(new Set());
    const managedTimeoutsRef = useRef(new Set());
    const hasShownInitialInteractionPromptRef = useRef(false);
    const glassHiddenForAR = useRef(false);
    const hasLaunchedAR = useRef(false);
    const [threeLoaded, setThreeLoaded] = useState(false);
    const [threeModules, setThreeModules] = useState(null);
    const [modelViewerReady, setModelViewerReady] = useState(
      () =>
        typeof window !== "undefined" &&
        Boolean(window.customElements?.get("model-viewer")),
    );
    const [frameDimensions, setFrameDimensions] = useState({
      width: 0,
      height: 0,
    });
    const [activeSlotIndex, setActiveSlotIndex] = useState(0);
    const [committedPreviewToken, setCommittedPreviewToken] = useState(0);
    const [arGLBReady, setArGLBReady] = useState(false);

    const scheduleManagedTimeout = useCallback((callback, delay) => {
      const timeoutId = window.setTimeout(() => {
        managedTimeoutsRef.current.delete(timeoutId);
        callback();
      }, delay);

      managedTimeoutsRef.current.add(timeoutId);
      return timeoutId;
    }, []);

    const revokeBlobUrl = useCallback((url) => {
      if (url && typeof url === "string" && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    }, []);

    const isStaleRenderToken = useCallback((token) => {
      return (
        token !== renderTokenRef.current ||
        discardedTokensRef.current.has(token)
      );
    }, []);

    const getActiveViewer = useCallback(() => {
      return viewerRefs.current[activeSlotRef.current];
    }, []);

    const setViewerInteractionPrompt = useCallback(
      (viewerElement, showPrompt) => {
        if (!viewerElement) {
          return;
        }

        if (showPrompt) {
          viewerElement.removeAttribute("interaction-prompt");
        } else {
          viewerElement.setAttribute("interaction-prompt", "none");
        }
      },
      [],
    );

    const handleARClick = useCallback(() => {
      trackEvent("View in space: AR Mode");

      const activeViewer = getActiveViewer();
      const committedPreview = committedPreviewRef.current;

      if (!activeViewer || !committedPreview.arUrl) {
        return;
      }

      setViewerInteractionPrompt(activeViewer, false);
      activeViewer.src = committedPreview.arUrl;
      glassHiddenForAR.current = true;

      scheduleManagedTimeout(() => {
        if (activeViewer.activateAR) {
          activeViewer.activateAR();
        }
      }, 100);
    }, [getActiveViewer, scheduleManagedTimeout, setViewerInteractionPrompt]);

    useImperativeHandle(
      ref,
      () => ({
        handleARClick,
      }),
      [handleARClick],
    );

    const disableARAccess = useCallback(() => {
      setArGLBReady(false);
      if (onARReady) {
        onARReady(null);
      }
    }, [onARReady]);

    const restoreCommittedARAccess = useCallback(() => {
      const hasCommittedAR = Boolean(committedPreviewRef.current.arUrl);

      setArGLBReady(hasCommittedAR);
      if (onARReady) {
        onARReady(hasCommittedAR ? handleARClick : null);
      }
    }, [handleARClick, onARReady]);

    const cleanupSlotListeners = useCallback((slotIndex) => {
      const slotState = viewerSlotStateRef.current[slotIndex];

      if (slotState.cleanupLoad) {
        slotState.cleanupLoad();
        slotState.cleanupLoad = null;
      }

      if (slotState.cleanupError) {
        slotState.cleanupError();
        slotState.cleanupError = null;
      }
    }, []);

    const clearPendingPreview = useCallback(
      ({ restoreAR = false } = {}) => {
        const pendingPreview = pendingPreviewRef.current;

        if (!pendingPreview) {
          if (restoreAR) {
            restoreCommittedARAccess();
          }
          return;
        }

        cleanupSlotListeners(pendingPreview.stagingIndex);

        const slotState =
          viewerSlotStateRef.current[pendingPreview.stagingIndex];
        if (
          slotState.browserUrl &&
          slotState.browserUrl === pendingPreview.browserUrl
        ) {
          slotState.browserUrl = null;
          slotState.token = 0;
        }

        const stagingViewer = viewerRefs.current[pendingPreview.stagingIndex];
        if (stagingViewer) {
          stagingViewer.removeAttribute("src");
        }

        revokeBlobUrl(pendingPreview.browserUrl);
        revokeBlobUrl(pendingPreview.arUrl);
        pendingPreviewRef.current = null;

        if (restoreAR) {
          restoreCommittedARAccess();
        }
      },
      [cleanupSlotListeners, restoreCommittedARAccess, revokeBlobUrl],
    );

    const commitARPreview = useCallback(
      (token, arUrl) => {
        if (
          isStaleRenderToken(token) ||
          committedPreviewRef.current.token !== token
        ) {
          revokeBlobUrl(arUrl);
          return;
        }

        const previousArUrl = committedPreviewRef.current.arUrl;
        if (previousArUrl && previousArUrl !== arUrl) {
          revokeBlobUrl(previousArUrl);
        }

        committedPreviewRef.current = {
          ...committedPreviewRef.current,
          arUrl,
        };

        setArGLBReady(true);
        if (onARReady) {
          onARReady(handleARClick);
        }
      },
      [handleARClick, isStaleRenderToken, onARReady, revokeBlobUrl],
    );

    const commitStagedPreview = useCallback(
      (token, stagingIndex) => {
        const pendingPreview = pendingPreviewRef.current;

        if (
          !pendingPreview ||
          pendingPreview.token !== token ||
          pendingPreview.stagingIndex !== stagingIndex ||
          isStaleRenderToken(token)
        ) {
          return;
        }

        cleanupSlotListeners(stagingIndex);
        const previousArUrl = committedPreviewRef.current.arUrl;

        activeSlotRef.current = stagingIndex;
        setActiveSlotIndex(stagingIndex);
        glassHiddenForAR.current = false;

        committedPreviewRef.current = {
          token,
          slotIndex: stagingIndex,
          browserUrl: pendingPreview.browserUrl,
          arUrl: null,
        };

        pendingPreviewRef.current = null;
        setCommittedPreviewToken(token);
        if (!hasShownInitialInteractionPromptRef.current) {
          hasShownInitialInteractionPromptRef.current = true;
        }

        if (setCanvasAssetsLoaded) {
          setCanvasAssetsLoaded();
        }

        if (pendingPreview.arUrl) {
          commitARPreview(token, pendingPreview.arUrl);
        } else {
          revokeBlobUrl(previousArUrl);
          disableARAccess();
        }
      },
      [
        cleanupSlotListeners,
        commitARPreview,
        disableARAccess,
        isStaleRenderToken,
        revokeBlobUrl,
        setCanvasAssetsLoaded,
      ],
    );

    const handleStagedPreviewError = useCallback(
      (token, stagingIndex, browserUrl, errorEvent) => {
        if (isStaleRenderToken(token)) {
          revokeBlobUrl(browserUrl);
          return;
        }

        discardedTokensRef.current.add(token);
        cleanupSlotListeners(stagingIndex);

        const slotState = viewerSlotStateRef.current[stagingIndex];
        if (slotState.browserUrl === browserUrl) {
          slotState.browserUrl = null;
          slotState.token = 0;
        }

        const stagingViewer = viewerRefs.current[stagingIndex];
        if (stagingViewer) {
          stagingViewer.removeAttribute("src");
        }

        const pendingPreview = pendingPreviewRef.current;
        if (pendingPreview?.token === token) {
          revokeBlobUrl(pendingPreview.arUrl);
          pendingPreviewRef.current = null;
        }

        revokeBlobUrl(browserUrl);

        if (setCanvasAssetsLoaded) {
          setCanvasAssetsLoaded();
        }
        restoreCommittedARAccess();

        console.error(
          "Failed to load staged model-viewer preview:",
          errorEvent,
        );
      },
      [
        cleanupSlotListeners,
        isStaleRenderToken,
        restoreCommittedARAccess,
        revokeBlobUrl,
        setCanvasAssetsLoaded,
      ],
    );

    const loadPreviewIntoStagingViewer = useCallback(
      (token, browserUrl) => {
        if (isStaleRenderToken(token)) {
          revokeBlobUrl(browserUrl);
          return;
        }

        const stagingIndex = activeSlotRef.current === 0 ? 1 : 0;
        const stagingViewer = viewerRefs.current[stagingIndex];
        if (!stagingViewer) {
          revokeBlobUrl(browserUrl);
          return;
        }

        cleanupSlotListeners(stagingIndex);

        const slotState = viewerSlotStateRef.current[stagingIndex];
        const committedPreview = committedPreviewRef.current;
        if (
          slotState.browserUrl &&
          !(
            committedPreview.slotIndex === stagingIndex &&
            committedPreview.token === slotState.token
          )
        ) {
          revokeBlobUrl(slotState.browserUrl);
        }

        slotState.browserUrl = browserUrl;
        slotState.token = token;
        stagingViewer.dataset.renderToken = String(token);
        setViewerInteractionPrompt(
          stagingViewer,
          !hasShownInitialInteractionPromptRef.current,
        );

        const handleLoad = () => {
          commitStagedPreview(token, stagingIndex);
        };
        const handleError = (event) => {
          handleStagedPreviewError(token, stagingIndex, browserUrl, event);
        };

        stagingViewer.addEventListener("load", handleLoad);
        stagingViewer.addEventListener("error", handleError);

        slotState.cleanupLoad = () => {
          stagingViewer.removeEventListener("load", handleLoad);
        };
        slotState.cleanupError = () => {
          stagingViewer.removeEventListener("error", handleError);
        };

        pendingPreviewRef.current = {
          token,
          stagingIndex,
          browserUrl,
          arUrl:
            pendingPreviewRef.current?.token === token
              ? pendingPreviewRef.current.arUrl
              : null,
        };

        stagingViewer.src = browserUrl;
      },
      [
        cleanupSlotListeners,
        commitStagedPreview,
        handleStagedPreviewError,
        isStaleRenderToken,
        revokeBlobUrl,
        setViewerInteractionPrompt,
      ],
    );

    const assignPreviewARUrl = useCallback(
      (token, arUrl) => {
        const pendingPreview = pendingPreviewRef.current;
        if (pendingPreview?.token === token) {
          pendingPreviewRef.current = {
            ...pendingPreview,
            arUrl,
          };
          return;
        }

        commitARPreview(token, arUrl);
      },
      [commitARPreview],
    );

    const configureViewerElement = useCallback(
      (viewerElement) => {
        if (!viewerElement) {
          return;
        }

        viewerElement.setAttribute("ar", "");
        viewerElement.setAttribute("ar-modes", "scene-viewer quick-look webxr");
        viewerElement.setAttribute("ar-scale", "fixed");
        viewerElement.setAttribute("scale", "1 1 1");
        viewerElement.setAttribute("exposure", "1.25");
        viewerElement.setAttribute("environment-image", hdr_environment);
        viewerElement.setAttribute("ar-button-hidden", "");

        if (!isMobile) {
          viewerElement.setAttribute("camera-controls", "");
          viewerElement.setAttribute("disable-zoom", "");
        } else {
          viewerElement.removeAttribute("camera-controls");
          viewerElement.removeAttribute("disable-zoom");
        }

        const maxDimension = Math.max(
          frameDimensions.width,
          frameDimensions.height,
        );
        const fillPercentage = 0.45;
        const fovRadians = (55 * Math.PI) / 180;
        const calculatedDistance =
          maxDimension > 0
            ? maxDimension / 2 / (Math.tan(fovRadians / 2) * fillPercentage)
            : 1;
        const distanceInMeters = Math.max(
          1.0,
          Math.min(5.0, calculatedDistance),
        );
        const cameraDistance = `${distanceInMeters.toFixed(1)}m`;

        if (!isMobile) {
          viewerElement.setAttribute(
            "min-camera-orbit",
            `-35deg 80deg ${cameraDistance}`,
          );
          viewerElement.setAttribute(
            "max-camera-orbit",
            `35deg 100deg ${cameraDistance}`,
          );
          viewerElement.setAttribute(
            "camera-orbit",
            `0deg 90deg ${cameraDistance}`,
          );
        } else {
          viewerElement.setAttribute(
            "camera-orbit",
            `0deg 90deg ${cameraDistance}`,
          );
          viewerElement.removeAttribute("min-camera-orbit");
          viewerElement.removeAttribute("max-camera-orbit");
        }
      },
      [frameDimensions.height, frameDimensions.width, hdr_environment],
    );

    const createViewerElement = useCallback(
      (slotIndex) => {
        const viewerElement = document.createElement("model-viewer");
        viewerElement.dataset.slotIndex = String(slotIndex);
        viewerElement.style.width = "100%";
        viewerElement.style.height = "100%";
        viewerElement.style.display = "block";
        viewerElement.style.userSelect = "none";
        viewerElement.style.overflow = "hidden";

        if (!isMobile) {
          viewerElement.style.cursor = "grab";
        }

        const disableRightClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          return false;
        };

        const preventScrollCapture = (event) => {
          if (
            event.type === "wheel" ||
            event.type === "mousewheel" ||
            event.type === "DOMMouseScroll"
          ) {
            event.stopPropagation();
          }
        };

        const onMouseDown = () => {
          if (!isMobile) {
            viewerElement.style.cursor = "grabbing";
          }
        };

        const onMouseUp = () => {
          if (!isMobile) {
            viewerElement.style.cursor = "grab";
          }
        };

        if (!isMobile) {
          viewerElement.addEventListener("wheel", preventScrollCapture, true);
          viewerElement.addEventListener(
            "mousewheel",
            preventScrollCapture,
            true,
          );
          viewerElement.addEventListener(
            "DOMMouseScroll",
            preventScrollCapture,
            true,
          );
          viewerElement.addEventListener("mousedown", onMouseDown);
          viewerElement.addEventListener("mouseup", onMouseUp);
          viewerElement.addEventListener("mouseleave", onMouseUp);
        }

        viewerElement.addEventListener("contextmenu", disableRightClick, false);
        configureViewerElement(viewerElement);

        viewerBaseCleanupRef.current[slotIndex] = () => {
          viewerElement.removeEventListener(
            "contextmenu",
            disableRightClick,
            false,
          );

          if (!isMobile) {
            viewerElement.removeEventListener(
              "wheel",
              preventScrollCapture,
              true,
            );
            viewerElement.removeEventListener(
              "mousewheel",
              preventScrollCapture,
              true,
            );
            viewerElement.removeEventListener(
              "DOMMouseScroll",
              preventScrollCapture,
              true,
            );
            viewerElement.removeEventListener("mousedown", onMouseDown);
            viewerElement.removeEventListener("mouseup", onMouseUp);
            viewerElement.removeEventListener("mouseleave", onMouseUp);
          }
        };

        return viewerElement;
      },
      [configureViewerElement],
    );

    const exportToModelViewer = useCallback(
      (token, exportGroup) => {
        if (!exportGroup || !threeModules) {
          return;
        }

        const { GLTFExporter } = threeModules;
        const exporter = new GLTFExporter();
        const exportOptions = {
          binary: true,
          embedImages: true,
          maxTextureSize: 2048,
          includeCustomExtensions: false,
          forcePowerOfTwoTextures: true,
        };

        const createARVersion = () => {
          let glassMesh = null;
          exportGroup.traverse((child) => {
            if (child.name === "glass") {
              glassMesh = child;
              child.visible = false;
            }
          });

          exporter.parse(
            exportGroup,
            (result) => {
              if (glassMesh) {
                glassMesh.visible = true;
              }

              if (!(result instanceof ArrayBuffer)) {
                return;
              }

              const arUrl = URL.createObjectURL(
                new Blob([result], { type: "model/gltf-binary" }),
              );

              if (isStaleRenderToken(token)) {
                revokeBlobUrl(arUrl);
                return;
              }

              assignPreviewARUrl(token, arUrl);
            },
            () => {
              if (glassMesh) {
                glassMesh.visible = true;
              }
            },
            exportOptions,
          );
        };

        exporter.parse(
          exportGroup,
          (result) => {
            if (!(result instanceof ArrayBuffer)) {
              return;
            }

            const browserUrl = URL.createObjectURL(
              new Blob([result], { type: "model/gltf-binary" }),
            );

            if (isStaleRenderToken(token)) {
              revokeBlobUrl(browserUrl);
              return;
            }

            loadPreviewIntoStagingViewer(token, browserUrl);
            createARVersion();
          },
          undefined,
          exportOptions,
        );
      },
      [
        assignPreviewARUrl,
        isStaleRenderToken,
        loadPreviewIntoStagingViewer,
        revokeBlobUrl,
        threeModules,
      ],
    );

    useEffect(() => {
      const loadThreeJS = async () => {
        try {
          const [threeModule, gltfExporterModule, rgbeLoaderModule] =
            await Promise.all([
              import("three"),
              import("three/examples/jsm/exporters/GLTFExporter.js"),
              import("three/examples/jsm/loaders/RGBELoader.js"),
            ]);

          setThreeModules({
            THREE: threeModule,
            TextureLoader: threeModule.TextureLoader,
            GLTFExporter: gltfExporterModule.GLTFExporter,
            RGBELoader: rgbeLoaderModule.RGBELoader,
            PMREMGenerator: threeModule.PMREMGenerator,
          });
          setThreeLoaded(true);
        } catch (error) {
          console.error("Failed to load Three.js:", error);
        }
      };

      loadThreeJS();
    }, []);

    useEffect(() => {
      if (modelViewerReady) return;

      let isActive = true;

      loadModuleScript(MODEL_VIEWER_SCRIPT_SRC)
        .then(() => {
          if (isActive) {
            setModelViewerReady(true);
          }
        })
        .catch((error) => {
          console.error("Failed to load model-viewer:", error);
        });

      return () => {
        isActive = false;
      };
    }, [modelViewerReady]);

    useEffect(() => {
      if (!modelViewerReady) {
        return undefined;
      }

      const hostRefs = [primaryViewerHostRef, secondaryViewerHostRef];
      hostRefs.forEach((hostRef, slotIndex) => {
        if (!hostRef.current || viewerRefs.current[slotIndex]) {
          return;
        }

        const viewerElement = createViewerElement(slotIndex);
        hostRef.current.appendChild(viewerElement);
        viewerRefs.current[slotIndex] = viewerElement;
      });

      if (!document.getElementById(MODEL_VIEWER_STYLE_ID)) {
        const style = document.createElement("style");
        style.id = MODEL_VIEWER_STYLE_ID;
        style.textContent = `
          model-viewer::part(default-ar-button) {
            display: none !important;
          }
          model-viewer .ar-button {
            display: none !important;
          }
        `;
        document.head.appendChild(style);
      }

      return () => undefined;
    }, [createViewerElement, modelViewerReady]);

    useEffect(() => {
      viewerRefs.current.forEach((viewerElement) => {
        configureViewerElement(viewerElement);
      });
    }, [configureViewerElement]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (!document.hidden && glassHiddenForAR.current) {
          const activeViewer = getActiveViewer();
          const committedPreview = committedPreviewRef.current;

          if (activeViewer && committedPreview.browserUrl) {
            setViewerInteractionPrompt(activeViewer, false);
            activeViewer.src = committedPreview.browserUrl;
            glassHiddenForAR.current = false;
          }
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }, [getActiveViewer, setViewerInteractionPrompt]);

    useEffect(() => {
      if (
        !launch_ar_mode ||
        !committedPreviewToken ||
        !arGLBReady ||
        hasLaunchedAR.current
      ) {
        return undefined;
      }

      hasLaunchedAR.current = true;
      const timeoutId = scheduleManagedTimeout(() => {
        handleARClick();
        if (onARLaunched) {
          onARLaunched();
        }
      }, 1000);

      return () => {
        window.clearTimeout(timeoutId);
        managedTimeoutsRef.current.delete(timeoutId);
      };
    }, [
      arGLBReady,
      committedPreviewToken,
      handleARClick,
      launch_ar_mode,
      onARLaunched,
      scheduleManagedTimeout,
    ]);

    useEffect(() => {
      if (!threeLoaded || !threeModules || !modelViewerReady) {
        return undefined;
      }

      const { THREE, TextureLoader, RGBELoader, PMREMGenerator } = threeModules;
      const renderToken = renderTokenRef.current + 1;
      renderTokenRef.current = renderToken;
      discardedTokensRef.current.delete(renderToken);

      clearPendingPreview();
      disableARAccess();

      let cancelled = false;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xffffff);
      sceneRef.current = scene;

      const SCALE_FACTOR = 0.001004;
      const frameWidth =
        (frame_style.width > 0 ? frame_style.width - 2 : 0) * SCALE_FACTOR;
      const matLeft = (mat_style.left - 5) * SCALE_FACTOR;
      const matRight = (mat_style.right - 5) * SCALE_FACTOR;
      const matTop = (mat_style.top - 5) * SCALE_FACTOR;
      const matBottom = (mat_style.bottom - 5) * SCALE_FACTOR;

      const artworkWidth = parseInt(width_mm, 10) * SCALE_FACTOR;
      const artworkHeight = parseInt(height_mm, 10) * SCALE_FACTOR;

      const framePositionOffset =
        frame_style.frame_type === "canvas" ? 16 * SCALE_FACTOR : 0;

      const totalWidth =
        artworkWidth +
        frameWidth * 2 +
        matLeft +
        matRight +
        framePositionOffset;
      const totalHeight =
        artworkHeight +
        frameWidth * 2 +
        matTop +
        matBottom +
        framePositionOffset;

      setFrameDimensions({ width: totalWidth, height: totalHeight });

      const frameDepth = frame_style.depth * SCALE_FACTOR;

      const camera = new THREE.PerspectiveCamera(
        55,
        totalWidth / totalHeight,
        0.1,
        1000,
      );

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      const canvasAspect = totalWidth / totalHeight;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.0;
      renderer.shadowMap.enabled = false;

      const pmremGenerator = new PMREMGenerator(renderer);
      pmremGenerator.compileEquirectangularShader();

      const applyEnvironmentToGlass = (envMap) => {
        scene.traverse((child) => {
          if (child.isMesh && child.material && child.name === "glass") {
            child.material.envMap = envMap;
            child.material.needsUpdate = true;
          }
        });
      };

      const createSimpleEnvironment = () => {
        const size = 512;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);

        const dataURL = canvas.toDataURL();
        const cubeTextureLoader = new THREE.CubeTextureLoader();
        const urls = [dataURL, dataURL, dataURL, dataURL, dataURL, dataURL];

        cubeTextureLoader.load(
          urls,
          (texture) => {
            texture.magFilter = THREE.LinearFilter;
            texture.minFilter = THREE.LinearFilter;
            texture.generateMipmaps = false;
            texture.needsUpdate = true;

            scene.environment = texture;
            window.currentEnvironment = texture;
            applyEnvironmentToGlass(texture);
          },
          undefined,
          (error) => {
            console.error("Failed to create fallback environment:", error);
          },
        );
      };

      const rgbeLoader = new RGBELoader();
      rgbeLoader.load(
        hdr_environment,
        (hdrMap) => {
          hdrMap.mapping = THREE.EquirectangularReflectionMapping;
          hdrMap.needsUpdate = true;
          scene.environment = hdrMap;
          window.currentEnvironment = hdrMap;
          applyEnvironmentToGlass(hdrMap);
        },
        undefined,
        (error) => {
          console.warn("Failed to load HDR environment map:", error);
          console.warn("HDR URL:", hdr_environment);
          createSimpleEnvironment();
        },
      );

      const textureLoader = new TextureLoader();

      const frameGroup = new THREE.Group();
      scene.add(frameGroup);
      frameGroup.position.set(-totalWidth / 2, -totalHeight / 2, 0);
      frameGroupRef.current = frameGroup;

      const createMiteredFramePiece = (length, width, depth) => {
        const shape = new THREE.Shape();
        const miter = width;

        shape.moveTo(miter, 0);
        shape.lineTo(length - miter, 0);
        shape.lineTo(length, width);
        shape.lineTo(0, width);
        shape.closePath();

        const geometry = new THREE.ExtrudeGeometry(shape, {
          depth,
          bevelEnabled: false,
          steps: 1,
        });

        geometry.translate(0, -width, -depth / 2);

        return new THREE.Mesh(
          geometry,
          new THREE.MeshStandardMaterial({
            color: 0x8b4513,
            roughness: 0.8,
            metalness: 0.1,
            side: THREE.DoubleSide,
          }),
        );
      };

      const debugX = 0;
      const debugY = totalHeight;
      const debugZ = 0;

      let topFrame;
      let bottomFrame;
      let leftFrame;
      let rightFrame;

      if (frame_style.width > 0) {
        topFrame = createMiteredFramePiece(totalWidth, frameWidth, frameDepth);
        topFrame.position.set(debugX, debugY, debugZ);
        frameGroup.add(topFrame);

        bottomFrame = createMiteredFramePiece(
          totalWidth,
          frameWidth,
          frameDepth,
        );
        bottomFrame.position.set(totalWidth, 0, debugZ);
        bottomFrame.rotation.z = Math.PI;
        frameGroup.add(bottomFrame);

        leftFrame = createMiteredFramePiece(
          totalHeight,
          frameWidth,
          frameDepth,
        );
        leftFrame.position.set(debugX, 0, debugZ);
        leftFrame.rotation.z = Math.PI / 2;
        frameGroup.add(leftFrame);

        rightFrame = createMiteredFramePiece(
          totalHeight,
          frameWidth,
          frameDepth,
        );
        rightFrame.position.set(totalWidth, debugY, debugZ);
        rightFrame.rotation.z = -Math.PI / 2;
        frameGroup.add(rightFrame);

        const backingGeometry = new THREE.PlaneGeometry(
          totalWidth,
          totalHeight,
        );
        const backingMaterial = new THREE.MeshStandardMaterial({
          color: 0x333333,
          side: THREE.DoubleSide,
          roughness: 1.0,
          metalness: 0.0,
        });
        const backingMesh = new THREE.Mesh(backingGeometry, backingMaterial);
        backingMesh.position.set(
          totalWidth / 2,
          totalHeight / 2,
          -5 * SCALE_FACTOR,
        );
        frameGroup.add(backingMesh);
      }

      if (mat_style.border !== "no_border") {
        const shadowWidth = 20 * SCALE_FACTOR;
        const topRightShadowOpacity = 0.2;
        const bottomLeftShadowOpacity = 0.05;

        const createShadowTexture = (direction, opacity) => {
          const canvas = document.createElement("canvas");
          canvas.width = 64;
          canvas.height = 64;
          const ctx = canvas.getContext("2d");

          const gradient = ctx.createLinearGradient(
            0,
            0,
            direction === "horizontal" ? canvas.width : 0,
            direction === "vertical" ? canvas.height : 0,
          );

          gradient.addColorStop(0, `rgba(0, 0, 0, ${opacity})`);
          gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          const texture = new THREE.CanvasTexture(canvas);
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          return texture;
        };

        const topShadow = new THREE.Mesh(
          new THREE.PlaneGeometry(totalWidth - frameWidth * 2, shadowWidth),
          new THREE.MeshStandardMaterial({
            map: createShadowTexture("vertical", topRightShadowOpacity),
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthWrite: false,
            roughness: 1.0,
            metalness: 0.0,
          }),
        );
        topShadow.position.set(
          frameWidth + (totalWidth - frameWidth * 2) / 2,
          totalHeight - frameWidth - shadowWidth / 2,
          5.5 * SCALE_FACTOR,
        );
        frameGroup.add(topShadow);

        const bottomShadow = new THREE.Mesh(
          new THREE.PlaneGeometry(totalWidth - frameWidth * 2, shadowWidth),
          new THREE.MeshStandardMaterial({
            map: createShadowTexture("vertical", bottomLeftShadowOpacity),
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthWrite: false,
            roughness: 1.0,
            metalness: 0.0,
          }),
        );
        bottomShadow.position.set(
          frameWidth + (totalWidth - frameWidth * 2) / 2,
          frameWidth + shadowWidth / 2,
          5.5 * SCALE_FACTOR,
        );
        bottomShadow.rotation.z = Math.PI;
        frameGroup.add(bottomShadow);

        const leftShadow = new THREE.Mesh(
          new THREE.PlaneGeometry(shadowWidth, totalHeight - frameWidth * 2),
          new THREE.MeshStandardMaterial({
            map: createShadowTexture("horizontal", bottomLeftShadowOpacity),
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthWrite: false,
            roughness: 1.0,
            metalness: 0.0,
          }),
        );
        leftShadow.position.set(
          frameWidth + shadowWidth / 2,
          frameWidth + (totalHeight - frameWidth * 2) / 2,
          5.5 * SCALE_FACTOR,
        );
        frameGroup.add(leftShadow);

        const rightShadow = new THREE.Mesh(
          new THREE.PlaneGeometry(shadowWidth, totalHeight - frameWidth * 2),
          new THREE.MeshStandardMaterial({
            map: createShadowTexture("horizontal", topRightShadowOpacity),
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthWrite: false,
            roughness: 1.0,
            metalness: 0.0,
          }),
        );
        rightShadow.position.set(
          totalWidth - frameWidth - shadowWidth / 2,
          frameWidth + (totalHeight - frameWidth * 2) / 2,
          5.5 * SCALE_FACTOR,
        );
        rightShadow.rotation.z = Math.PI;
        frameGroup.add(rightShadow);

        const innerMatWidth = artworkWidth;
        const innerMatHeight = artworkHeight;

        if (mat_style.border === "mat_border" && mat.core_colour) {
          const matThickness = 3.5 * SCALE_FACTOR;
          const bevelWidth = 2.5 * SCALE_FACTOR;

          const matColour = new THREE.Color(mat.colour || "#ffffff");

          const matSurfaceMaterial = new THREE.MeshStandardMaterial({
            color: matColour,
            roughness: 0.5,
            metalness: 0.0,
          });

          const bevelLightMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color("#dddddd"),
            roughness: 0.6,
            metalness: 0.0,
            side: THREE.DoubleSide,
          });
          const bevelDarkMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color("#cccccc"),
            roughness: 0.6,
            metalness: 0.0,
            side: THREE.DoubleSide,
          });

          const fullMatWidth = innerMatWidth + matLeft + matRight;
          const fullMatHeight = innerMatHeight + matTop + matBottom;

          const outerShape = new THREE.Shape();
          outerShape.moveTo(0, 0);
          outerShape.lineTo(fullMatWidth, 0);
          outerShape.lineTo(fullMatWidth, fullMatHeight);
          outerShape.lineTo(0, fullMatHeight);
          outerShape.closePath();

          const hole = new THREE.Path();
          hole.moveTo(matLeft, matBottom);
          hole.lineTo(matLeft + innerMatWidth, matBottom);
          hole.lineTo(matLeft + innerMatWidth, matBottom + innerMatHeight);
          hole.lineTo(matLeft, matBottom + innerMatHeight);
          hole.closePath();
          outerShape.holes.push(hole);

          const matGeometry = new THREE.ExtrudeGeometry(outerShape, {
            depth: matThickness,
            bevelEnabled: false,
            steps: 1,
          });

          const matMesh = new THREE.Mesh(matGeometry, matSurfaceMaterial);
          matMesh.position.set(frameWidth, frameWidth, 0);
          frameGroup.add(matMesh);

          const ix = matLeft;
          const iy = matBottom;
          const iw = innerMatWidth;
          const ih = innerMatHeight;
          const bz = matThickness + 0.1 * SCALE_FACTOR;

          const topBevelShape = new THREE.Shape();
          topBevelShape.moveTo(ix, iy + ih);
          topBevelShape.lineTo(ix + iw, iy + ih);
          topBevelShape.lineTo(ix + iw - bevelWidth, iy + ih - bevelWidth);
          topBevelShape.lineTo(ix + bevelWidth, iy + ih - bevelWidth);
          topBevelShape.closePath();
          const topBevel = new THREE.Mesh(
            new THREE.ShapeGeometry(topBevelShape),
            bevelLightMaterial,
          );
          topBevel.position.set(frameWidth, frameWidth, bz);
          frameGroup.add(topBevel);

          const bottomBevelShape = new THREE.Shape();
          bottomBevelShape.moveTo(ix, iy);
          bottomBevelShape.lineTo(ix + iw, iy);
          bottomBevelShape.lineTo(ix + iw - bevelWidth, iy + bevelWidth);
          bottomBevelShape.lineTo(ix + bevelWidth, iy + bevelWidth);
          bottomBevelShape.closePath();
          const bottomBevel = new THREE.Mesh(
            new THREE.ShapeGeometry(bottomBevelShape),
            bevelDarkMaterial,
          );
          bottomBevel.position.set(frameWidth, frameWidth, bz);
          frameGroup.add(bottomBevel);

          const leftBevelShape = new THREE.Shape();
          leftBevelShape.moveTo(ix, iy);
          leftBevelShape.lineTo(ix, iy + ih);
          leftBevelShape.lineTo(ix + bevelWidth, iy + ih - bevelWidth);
          leftBevelShape.lineTo(ix + bevelWidth, iy + bevelWidth);
          leftBevelShape.closePath();
          const leftBevel = new THREE.Mesh(
            new THREE.ShapeGeometry(leftBevelShape),
            bevelDarkMaterial,
          );
          leftBevel.position.set(frameWidth, frameWidth, bz);
          frameGroup.add(leftBevel);

          const rightBevelShape = new THREE.Shape();
          rightBevelShape.moveTo(ix + iw, iy);
          rightBevelShape.lineTo(ix + iw, iy + ih);
          rightBevelShape.lineTo(ix + iw - bevelWidth, iy + ih - bevelWidth);
          rightBevelShape.lineTo(ix + iw - bevelWidth, iy + bevelWidth);
          rightBevelShape.closePath();
          const rightBevel = new THREE.Mesh(
            new THREE.ShapeGeometry(rightBevelShape),
            bevelLightMaterial,
          );
          rightBevel.position.set(frameWidth, frameWidth, bz);
          frameGroup.add(rightBevel);
        }
      }

      if (frame_style.frame_type === "standard") {
        const glassGeometry = new THREE.PlaneGeometry(
          totalWidth - frameWidth * 2,
          totalHeight - frameWidth * 2,
        );
        const glassMaterial = new THREE.MeshPhysicalMaterial({
          transmission: 1,
          transparent: true,
          opacity: 0.7,
          roughness: 0,
          metalness: 0.0,
          clearcoat: 1.0,
          clearcoatRoughness: 0.01,
          ior: 1.52,
          side: THREE.DoubleSide,
          envMapIntensity: 0.1,
          reflectivity: 0.1,
        });

        const glassMesh = new THREE.Mesh(glassGeometry, glassMaterial);
        glassMesh.position.set(
          frameWidth + (totalWidth - frameWidth * 2) / 2,
          frameWidth + (totalHeight - frameWidth * 2) / 2,
          6 * SCALE_FACTOR,
        );
        glassMesh.name = "glass";
        frameGroup.add(glassMesh);

        if (window.currentEnvironment) {
          glassMaterial.envMap = window.currentEnvironment;
          glassMaterial.needsUpdate = true;
        }
      }

      const framePieces = [topFrame, bottomFrame, leftFrame, rightFrame];

      const appendCacheBuster = (url, suffix) => {
        if (!url) return url;
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}t=${Date.now()}-${suffix}`;
      };

      const sleep = (ms) =>
        new Promise((resolve) => {
          scheduleManagedTimeout(resolve, ms);
        });

      const loadTextureOnce = (url, timeoutMs, label) =>
        new Promise((resolve, reject) => {
          let settled = false;
          const timeoutId = scheduleManagedTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error(`${label} load timed out after ${timeoutMs}ms`));
          }, timeoutMs);

          textureLoader.load(
            url,
            (texture) => {
              if (settled) {
                texture.dispose();
                return;
              }

              settled = true;
              window.clearTimeout(timeoutId);
              managedTimeoutsRef.current.delete(timeoutId);

              if (cancelled) {
                texture.dispose();
                reject(new Error("Texture load cancelled"));
                return;
              }

              resolve(texture);
            },
            undefined,
            (error) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeoutId);
              managedTimeoutsRef.current.delete(timeoutId);
              reject(
                error instanceof Error
                  ? error
                  : new Error(`${label} failed to load`),
              );
            },
          );
        });

      const loadTextureWithRetry = async (
        url,
        { label, maxRetries, timeoutMs },
      ) => {
        let lastError;

        for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
          const urlForAttempt =
            attempt === 0 ? url : appendCacheBuster(url, `retry-${attempt}`);

          try {
            return await loadTextureOnce(urlForAttempt, timeoutMs, label);
          } catch (error) {
            if (cancelled || error?.message === "Texture load cancelled") {
              throw error;
            }

            lastError = error;
            if (attempt >= maxRetries) {
              break;
            }

            await sleep(Math.min(2000, 400 * 2 ** attempt));
            if (cancelled) {
              throw new Error("Texture load cancelled");
            }
          }
        }

        throw (
          lastError ||
          new Error(`${label} failed after ${maxRetries + 1} attempts`)
        );
      };

      const createFrameMaterialSet = ({
        faceColor = new THREE.Color(0xffffff),
        edgeColor = new THREE.Color(0xffffff),
        map,
        edgeMap,
        unlitEdge = false,
      }) => {
        const faceMaterial = new THREE.MeshStandardMaterial({
          color: faceColor,
          map,
          roughness: 0.8,
          metalness: 0.1,
          side: THREE.DoubleSide,
        });
        const outsideMaterial = unlitEdge
          ? new THREE.MeshBasicMaterial({
              color: edgeColor,
              side: THREE.DoubleSide,
              toneMapped: false,
            })
          : new THREE.MeshStandardMaterial({
              color: edgeColor,
              map: edgeMap,
              roughness: 0.8,
              metalness: 0.1,
              side: THREE.DoubleSide,
            });

        return [faceMaterial, outsideMaterial];
      };

      const normalizedFrameEdgeColour = () => {
        const value =
          typeof frame_style.edge_colour === "string"
            ? frame_style.edge_colour.trim()
            : "";

        if (!/^#?[0-9a-fA-F]{6}$/.test(value)) {
          return null;
        }

        return value.startsWith("#") ? value : `#${value}`;
      };

      const createFrameEdgeColor = () => {
        const edgeColour = normalizedFrameEdgeColour();
        return edgeColour ? new THREE.Color(edgeColour) : null;
      };

      const applyFallbackFrameMaterials = () => {
        if (frame_style.width <= 0) return;

        const fallbackFaceColor = new THREE.Color(0x8b4513);
        const fixedEdgeColor = createFrameEdgeColor();
        const fallbackEdgeColor = fixedEdgeColor || fallbackFaceColor;

        framePieces.forEach((piece) => {
          if (!piece) return;
          piece.material = createFrameMaterialSet({
            faceColor: fallbackFaceColor,
            edgeColor: fallbackEdgeColor,
            unlitEdge: Boolean(fixedEdgeColor),
          });
        });
      };

      const applyFrameTexture = (frameTexture) => {
        if (frame_style.width <= 0 || cancelled) return;

        const imageWidth =
          frameTexture.image?.naturalWidth || frameTexture.image?.width;
        const imageHeight =
          frameTexture.image?.naturalHeight || frameTexture.image?.height;
        const faceTextureRepeat = calculateFrameFaceTextureRepeat({
          frameWidth,
          imageWidth,
          imageHeight,
        });

        frameTexture.wrapS = THREE.RepeatWrapping;
        frameTexture.wrapT = THREE.ClampToEdgeWrapping;
        frameTexture.minFilter = THREE.LinearMipMapLinearFilter;
        frameTexture.magFilter = THREE.LinearFilter;
        frameTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        frameTexture.colorSpace = THREE.SRGBColorSpace;
        frameTexture.repeat.set(faceTextureRepeat.x, faceTextureRepeat.y);
        frameTexture.needsUpdate = true;

        const createOutsideTexture = () => {
          const outsideTexture = frameTexture.clone();
          outsideTexture.wrapT = THREE.MirroredRepeatWrapping;
          outsideTexture.wrapS = THREE.MirroredRepeatWrapping;
          outsideTexture.repeat.set(10, 2000 / frame_style.depth);
          outsideTexture.offset.set(1, 1.6);
          outsideTexture.needsUpdate = true;
          return outsideTexture;
        };

        const createFaceMap = (flipU) => {
          const map = frameTexture.clone();
          if (flipU) {
            // Mirror the pattern along the rail's length so this rail runs in
            // the same direction as the rail opposite it (which is placed at a
            // 180° rotation). Without this, opposite sides appear flipped.
            map.wrapS = THREE.RepeatWrapping;
            map.repeat.x = -map.repeat.x;
            map.needsUpdate = true;
          }
          return map;
        };

        const createPieceMaterial = (flipU = false) => {
          const edgeColor = createFrameEdgeColor();
          if (edgeColor) {
            return createFrameMaterialSet({
              map: createFaceMap(flipU),
              edgeColor,
              unlitEdge: true,
            });
          }

          return createFrameMaterialSet({
            map: createFaceMap(flipU),
            edgeMap: createOutsideTexture(),
          });
        };

        // framePieces = [topFrame, bottomFrame, leftFrame, rightFrame].
        // Mirror the bottom + right rails (each rotated 180° from its partner)
        // so opposite sides of the frame run in the same direction.
        framePieces.forEach((piece, index) => {
          if (!piece) return;
          const flipU = index === 1 || index === 3;
          piece.material = createPieceMaterial(flipU);
        });
      };

      const sampleEdgeColor = (texture) => {
        if (!texture.image) return 0xffffff;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = texture.image.width;
        canvas.height = texture.image.height;
        ctx.drawImage(texture.image, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        let totalR = 0;
        let totalG = 0;
        let totalB = 0;
        let sampleCount = 0;

        const edgeX = canvas.width - 1;
        for (let y = 0; y < canvas.height; y += 10) {
          const index = (y * canvas.width + edgeX) * 4;
          totalR += data[index];
          totalG += data[index + 1];
          totalB += data[index + 2];
          sampleCount += 1;
        }

        const edgeY = canvas.height - 1;
        for (let x = 0; x < canvas.width; x += 10) {
          const index = (edgeY * canvas.width + x) * 4;
          totalR += data[index];
          totalG += data[index + 1];
          totalB += data[index + 2];
          sampleCount += 1;
        }

        const avgR = Math.round(totalR / sampleCount);
        const avgG = Math.round(totalG / sampleCount);
        const avgB = Math.round(totalB / sampleCount);

        const darkenedR = Math.round(avgR * 0.6);
        const darkenedG = Math.round(avgG * 0.6);
        const darkenedB = Math.round(avgB * 0.6);

        return (darkenedR << 16) | (darkenedG << 8) | darkenedB;
      };

      const loadArtworkTexture = async () => {
        if (!preview_url) return;

        try {
          const texture = await loadTextureWithRetry(preview_url, {
            label: "Artwork texture",
            maxRetries: 1,
            timeoutMs: 10000,
          });

          if (cancelled) {
            texture.dispose();
            return;
          }

          texture.colorSpace = THREE.SRGBColorSpace;
          texture.anisotropy = Math.min(
            4,
            renderer.capabilities.getMaxAnisotropy(),
          );
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.flipY = true;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          texture.needsUpdate = true;

          const artworkPositionOffset =
            frame_style.frame_type === "canvas"
              ? frameDepth - 23 * SCALE_FACTOR
              : 2.5 * SCALE_FACTOR;

          const artworkGeometry = new THREE.BoxGeometry(
            artworkWidth,
            artworkHeight,
            artworkPositionOffset,
          );
          const edgeColor = sampleEdgeColor(texture);
          const artworkMaterials = [
            new THREE.MeshStandardMaterial({ color: edgeColor }),
            new THREE.MeshStandardMaterial({ color: edgeColor }),
            new THREE.MeshStandardMaterial({ color: edgeColor }),
            new THREE.MeshStandardMaterial({ color: edgeColor }),
            new THREE.MeshStandardMaterial({
              map: texture,
              side: THREE.DoubleSide,
            }),
            new THREE.MeshStandardMaterial({ color: edgeColor }),
          ];

          const artworkMesh = new THREE.Mesh(artworkGeometry, artworkMaterials);
          artworkMesh.name = "artwork";
          artworkMesh.position.set(
            frameWidth + matLeft + artworkWidth / 2 + framePositionOffset / 2,
            frameWidth +
              matBottom +
              artworkHeight / 2 +
              framePositionOffset / 2,
            artworkPositionOffset / 2,
          );
          frameGroup.add(artworkMesh);
        } catch (error) {
          if (!cancelled) {
            console.error("Error loading artwork texture:", error);
          }
        }
      };

      const loadAndApplyFrameTexture = async () => {
        if (frame_style.width <= 0) return;

        const patternURL = frame_style.pattern_url || "";
        if (!patternURL) {
          applyFallbackFrameMaterials();
          return;
        }

        try {
          const frameTexture = await loadTextureWithRetry(patternURL, {
            label: "Frame texture",
            maxRetries: 2,
            timeoutMs: 8000,
          });

          if (cancelled) {
            frameTexture.dispose();
            return;
          }

          applyFrameTexture(frameTexture);
        } catch (error) {
          if (!cancelled) {
            console.error("Error loading frame texture:", error);
            applyFallbackFrameMaterials();
          }
        }
      };

      Promise.all([loadArtworkTexture(), loadAndApplyFrameTexture()]).finally(
        () => {
          if (cancelled || isStaleRenderToken(renderToken)) {
            return;
          }

          exportToModelViewer(renderToken, frameGroup);
        },
      );

      const handleResize = () => {
        let newWidth;
        let newHeight;

        if (canvasAspect >= 1) {
          newWidth = Math.min(maxWidth, window.innerWidth * 0.9);
          newHeight = newWidth / canvasAspect;
        } else {
          newHeight = Math.min(maxWidth, window.innerHeight * 0.9);
          newWidth = newHeight * canvasAspect;
        }

        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);
      };

      window.addEventListener("resize", handleResize);

      return () => {
        cancelled = true;
        window.removeEventListener("resize", handleResize);

        scene.traverse((object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }

          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => {
                if (material.map) material.map.dispose();
                material.dispose();
              });
            } else {
              if (object.material.map) object.material.map.dispose();
              object.material.dispose();
            }
          }
        });

        pmremGenerator.dispose();
        renderer.dispose();
      };
    }, [
      disableARAccess,
      exportToModelViewer,
      frame_style?.depth,
      frame_style?.edge_colour,
      frame_style?.frame_type,
      frame_style?.pattern_url,
      frame_style?.width,
      hdr_environment,
      height_mm,
      isStaleRenderToken,
      mat,
      mat_style,
      maxWidth,
      modelViewerReady,
      preview_url,
      scheduleManagedTimeout,
      threeLoaded,
      threeModules,
      width_mm,
      clearPendingPreview,
    ]);

    useEffect(
      () => () => {
        managedTimeoutsRef.current.forEach((timeoutId) => {
          window.clearTimeout(timeoutId);
        });
        managedTimeoutsRef.current.clear();

        clearPendingPreview();

        viewerSlotStateRef.current.forEach((slotState, slotIndex) => {
          cleanupSlotListeners(slotIndex);
          revokeBlobUrl(slotState.browserUrl);
          slotState.browserUrl = null;
          slotState.token = 0;
        });

        const committedPreview = committedPreviewRef.current;
        revokeBlobUrl(committedPreview.browserUrl);
        revokeBlobUrl(committedPreview.arUrl);

        viewerBaseCleanupRef.current.forEach((cleanup, slotIndex) => {
          if (cleanup) {
            cleanup();
          }

          const viewerElement = viewerRefs.current[slotIndex];
          if (viewerElement) {
            viewerElement.remove();
          }
        });

        const existingStyle = document.getElementById(MODEL_VIEWER_STYLE_ID);
        if (existingStyle) {
          existingStyle.remove();
        }
      },
      [clearPendingPreview, cleanupSlotListeners, revokeBlobUrl],
    );

    return (
      <div className="flex flex-col items-center w-full">
        <div className="relative w-full aspect-1">
          <div
            ref={primaryViewerHostRef}
            className={`absolute inset-0 ${
              activeSlotIndex === 0
                ? "z-[1]"
                : "pointer-events-none z-0 opacity-0"
            }`}
          />
          <div
            ref={secondaryViewerHostRef}
            className={`absolute inset-0 ${
              activeSlotIndex === 1
                ? "z-[1]"
                : "pointer-events-none z-0 opacity-0"
            }`}
          />
        </div>
      </div>
    );
  },
);

export default ThreeJSCanvas;
